import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { APP_NAME } from '../../constants';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        // Extract access token and state from URL hash
        const hash = location?.hash?.substring(1);
        const params = new URLSearchParams(hash);
        const accessToken = params?.get('access_token');
        const state = params?.get('state');
        const errorParam = params?.get('error');

        if (errorParam) {
          setError(`Erreur d'authentification: ${errorParam}`);
          setStatus('error');
          setTimeout(() => navigate('/login-authentication'), 3000);
          return;
        }

        if (!accessToken) {
          setError('Aucun token d\'accès reçu');
          setStatus('error');
          setTimeout(() => navigate('/login-authentication'), 3000);
          return;
        }

        // Fetch user profile based on provider
        let userProfile = null;
        if (state === 'google') {
          const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          });
          userProfile = await response?.json();
        } else if (state === 'facebook') {
          const response = await fetch(`https://graph.facebook.com/me?fields=id,name,email,picture&access_token=${accessToken}`);
          userProfile = await response?.json();
        }

        if (userProfile) {
          // Store user data
          localStorage.setItem('userRole', 'customer');
          localStorage.setItem('userEmail', userProfile?.email || 'oauth-user@plumberpro.fr');
          localStorage.setItem('userName', userProfile?.name || 'Utilisateur OAuth');
          localStorage.setItem('userAvatar', userProfile?.picture?.data?.url || userProfile?.picture || '');
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('authProvider', state);
          localStorage.setItem('accessToken', accessToken);

          setStatus('success');
          setTimeout(() => navigate('/customer-account-portal'), 2000);
        } else {
          throw new Error('Impossible de récupérer le profil utilisateur');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setError(err?.message || 'Une erreur est survenue lors de l\'authentification');
        setStatus('error');
        setTimeout(() => navigate('/login-authentication'), 3000);
      }
    };

    processOAuthCallback();
  }, [location, navigate]);

  return (
    <>
      <Helmet>
        <title>Authentification - {APP_NAME}</title>
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-md p-8">
          <div className="text-center">
            {status === 'processing' && (
              <>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <Icon name="Key" size={32} color="var(--color-primary)" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">
                  Authentification en cours...
                </h2>
                <p className="text-sm text-muted-foreground">
                  Veuillez patienter pendant que nous vérifions vos informations.
                </p>
                <div className="mt-6 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} color="var(--color-success)" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">
                  Authentification réussie !
                </h2>
                <p className="text-sm text-muted-foreground">
                  Redirection vers votre espace client...
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" size={32} color="var(--color-error)" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">
                  Échec de l'authentification
                </h2>
                <p className="text-sm text-error">
                  {error}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Redirection vers la page de connexion...
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthCallback;